import { useState } from 'react';
import type { PatientView } from '../../lib/types';
import { AppMark, Icon } from '../../components/Mark';
import { goalLabel } from '@oncocoach/care';
import { fmtDate, fmtSchedule } from '../../lib/format';
import { firstName } from '../../lib/session';

/**
 * Four panels the first time: who prescribed this, your profile, how it works, and all set.
 * A centred wizard card on a pale ground — everything shown comes from the patient's own record.
 */
export function Onboarding({ view, onDone }: { view: PatientView | null; onDone: () => void }) {
  const [step, setStep] = useState(0);
  const last = 3;

  return (
    <div className="onboard-bg">
      <div className="onboard">
        {step === 0 ? <Welcome view={view} /> : null}
        {step === 1 ? <Profile view={view} /> : null}
        {step === 2 ? <HowItWorks doctor={view?.doctor.name ?? 'your oncology team'} /> : null}
        {step === 3 ? <AllSet view={view} /> : null}
        <footer className="onboard-foot">
          <button type="button" className="quiet" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}>
            Back
          </button>
          <span className="dots" aria-label={`Step ${step + 1} of 4`}>
            {[0, 1, 2, 3].map((i) => (
              <i key={i} className={i === step ? 'on' : ''} />
            ))}
          </span>
          <button type="button" className="primary" onClick={() => (step === last ? onDone() : setStep(step + 1))}>
            {step === last ? 'Start my coaching →' : step === 2 ? "Let's go →" : 'Next →'}
          </button>
        </footer>
      </div>
    </div>
  );
}

function Welcome({ view }: { view: PatientView | null }) {
  return (
    <div className="onboard-panel hero-panel">
      <div>
        <span className="eyebrow">Someone on your side, between appointments.</span>
        <h1 className="brand-title" style={{ marginTop: '0.6rem' }}>
          Welcome to OncoCoach
        </h1>
        <p className="hint" style={{ marginTop: '0.75rem', fontSize: '1rem' }}>
          {view ? `${view.doctor.name.replace(/, MD$/, '')} has enrolled you in OncoCoach, so your team can see how you are doing between appointments.` : 'Your oncologist has enrolled you in OncoCoach.'}
        </p>
      </div>
      <div>
        <div className="rings">
          <AppMark size={46} />
        </div>
        <div className="prescribed">
          <span className="eyebrow">
            <span className="asterisk">✱</span> Prescribed by
          </span>
          <strong className="big-name">{view?.doctor.name ?? '…'}</strong>
          <span className="hint">{view?.practiceName ?? ''}</span>
        </div>
      </div>
    </div>
  );
}

function Profile({ view }: { view: PatientView | null }) {
  return (
    <div className="onboard-panel">
      <span className="eyebrow">Your profile</span>
      <h1 style={{ margin: '0.5rem 0 0.2rem' }}>
        {view?.profile.name ?? '…'}
        {view?.profile.age ? `, ${view.profile.age}` : ''}
      </h1>
      <span className="hint">Member since {view ? fmtDate(view.profile.memberSince) : '…'}</span>

      <div className="plan-grid">
        <div>
          <span className="eyebrow">Treatment</span>
          {view?.profile.cancerType ? <p className="hint">{view.profile.cancerType}</p> : null}
          {view?.profile.treatments.map((t) => (
            <div className="row" key={t.code}>
              <span className="bubble bubble-red bubble-sm">
                <Icon name="cycle" size={16} />
              </span>
              <span className="row-main">
                <span className="row-title">{t.label}</span>
                <span className="row-sub">{t.regimen ?? t.focus}</span>
              </span>
            </div>
          ))}
        </div>
        <div>
          <span className="eyebrow">Medications</span>
          {view?.medications.map((m) => (
            <div className="row" key={m.id}>
              <span className="dot-red" aria-hidden="true" />
              <span className="row-main">
                <span className="row-title">{m.name}</span>
                <span className="row-sub">{fmtSchedule(m.times)}</span>
              </span>
            </div>
          ))}
        </div>
        <div>
          <span className="eyebrow">Care goals</span>
          {view?.goals.map((g, i) => (
            <div className="row" key={i}>
              <span className={`bubble bubble-sm ${g.kind === 'daily-temperature' ? 'bubble-red' : g.kind === 'steps' ? 'bubble-green' : 'bubble-purple'}`}>
                <Icon name={g.kind === 'daily-temperature' ? 'temperature' : g.kind === 'steps' ? 'steps' : g.kind === 'intake' ? 'drop' : 'pill'} size={16} />
              </span>
              <span className="row-main">
                <span className="row-title">
                  {`${goalLabel(g).title} ${goalLabel(g).target}`}
                </span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function HowItWorks({ doctor }: { doctor: string }) {
  const items: Array<{ icon: Parameters<typeof Icon>[0]['name']; tone: string; title: string; text: string }> = [
    { icon: 'temperature', tone: 'red', title: 'Your temperature, every morning', text: 'On treatment it is the one number that says whether today is an ordinary day. Two taps, and your team sees it.' },
    { icon: 'symptoms', tone: 'gold', title: 'How you actually feel', text: 'Not a tick box: how much each thing is bothering you, so your team can tell a rough day from a dangerous one.' },
    { icon: 'chat', tone: 'green', title: 'A coach that has read your notes', text: 'Ask about your treatment, your tablets or a symptom. It answers from your own record, and it never guesses at a dose.' },
    { icon: 'shield', tone: 'blue', title: 'It knows when to stop talking', text: `OncoCoach never replaces ${doctor}. A fever on treatment, breathlessness or bleeding and it tells you to ring the emergency line now rather than answering — and flags it for your team.` },
  ];
  return (
    <div className="onboard-panel">
      <span className="eyebrow">How it works</span>
      <h1 style={{ margin: '0.5rem 0 0' }}>What this is for</h1>
      <div className="how-grid">
        {items.map((it) => (
          <div className="how" key={it.title}>
            <span className={`bubble bubble-${it.tone}`}>
              <Icon name={it.icon} />
            </span>
            <span className="row-main">
              <strong>{it.title}</strong>
              <span className="hint">{it.text}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AllSet({ view }: { view: PatientView | null }) {
  const name = view ? firstName(view.profile.name) : 'there';
  return (
    <div className="onboard-panel navy-panel">
      <div className="set-grid">
        <div>
          <span className="bubble bubble-xl bubble-red">
            <AppMark size={40} />
          </span>
          <h1 style={{ margin: '1.1rem 0 0.5rem', fontSize: '2rem' }}>You're all set, {name}.</h1>
          <p className="hint light">
            Your free OncoCoach account is ready. {view?.doctor.name.replace(/, MD$/, '') ?? 'Your doctor'}'s team can see your progress in real time.
          </p>
          <div className="trio">
            <div>
              <Icon name="check" size={22} />
              <strong>Free</strong>
              <span>Forever</span>
            </div>
            <div>
              <Icon name="chat" size={22} />
              <strong>24/7</strong>
              <span>AI Coach</span>
            </div>
            <div>
              <Icon name="star" size={22} />
              <strong>&lt; 1 min</strong>
              <span>Daily</span>
            </div>
          </div>
        </div>
        <div className="card card-navy quote">
          <h2 style={{ fontSize: '1.05rem', margin: '0 0 0.8rem' }}>Three things to know</h2>
          <ul className="plain">
            <li>What you log goes to {view?.practiceName ?? 'your practice'}. That is the point of it: they see trouble early.</li>
            <li>Your coach is an AI. It never changes your medications, and it never replaces {view?.doctor.name.replace(/, MD$/, '') ?? 'your oncology team'}.</li>
            <li>For a fever of 38.0°C or more on treatment, trouble breathing, bleeding or confusion, ring your oncology emergency line or go to the emergency department. Do not wait for an answer here.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
